import type {
  CmmdBoardTask,
  CmmdProjectTaskAssignmentSubject,
  CmmdTaskWorkflow,
} from "@cmmd-forge/contracts";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import {
  activeTasksInOrder,
  flattenTaskTree,
  type ProjectTaskBoardMove,
  type ProjectTaskPositionMove,
} from "./taskViewTypes";
import { assignmentConfirmed } from "./projectTaskAssignmentPatch";
import type { AssignmentOperation } from "./projectTaskAssignmentSelection";

// Projects owns its local planning-data errors. A stale CMMD REST JWT should
// not log the user out of the primary Forge session — so use a dedicated client
// instance, matching useCmmdProjectHome.
export const api = createCmmdApiClient();

export interface PendingAssignment {
  readonly fingerprint: string;
  readonly idempotencyKey: string;
}

export interface TaskBoardState {
  readonly tasks: readonly CmmdBoardTask[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly mutationError: string | null;
  readonly pendingTaskIds: ReadonlySet<number>;
  readonly creating: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly workflowMutationBlocked: boolean;
}

export type TaskBoardAction =
  | { type: "load-start" }
  | {
      type: "load-ok";
      tasks: readonly CmmdBoardTask[];
      workflow: CmmdTaskWorkflow | null;
      workflowMutationBlocked: boolean;
      workflowWarning: string | null;
    }
  | { type: "load-error"; message: string }
  | { type: "reconcile"; tasks: readonly CmmdBoardTask[] }
  | { type: "optimistic"; id: number; patch: Partial<CmmdBoardTask> }
  | { type: "settle"; id: number }
  | { type: "rollback"; snapshot: readonly CmmdBoardTask[]; id: number; message: string }
  | { type: "mutation-error"; message: string }
  | { type: "creating"; value: boolean };

function withPending(set: ReadonlySet<number>, id: number, on: boolean): Set<number> {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}

export function taskBoardReducer(state: TaskBoardState, action: TaskBoardAction): TaskBoardState {
  switch (action.type) {
    case "load-start":
      return { ...state, loading: true, error: null };
    case "load-ok":
      return {
        ...state,
        loading: false,
        tasks: action.tasks,
        workflow: action.workflow,
        workflowMutationBlocked: action.workflowMutationBlocked,
        mutationError: action.workflowWarning,
      };
    case "load-error":
      return { ...state, loading: false, error: action.message };
    case "reconcile":
      return { ...state, tasks: action.tasks, pendingTaskIds: new Set() };
    case "optimistic":
      return {
        ...state,
        mutationError: null,
        pendingTaskIds: withPending(state.pendingTaskIds, action.id, true),
        tasks: updateTaskTree(state.tasks, action.id, action.patch),
      };
    case "settle":
      return { ...state, pendingTaskIds: withPending(state.pendingTaskIds, action.id, false) };
    case "rollback":
      return {
        ...state,
        tasks: action.snapshot,
        mutationError: action.message,
        pendingTaskIds: withPending(state.pendingTaskIds, action.id, false),
      };
    case "mutation-error":
      return { ...state, mutationError: action.message };
    case "creating":
      return {
        ...state,
        creating: action.value,
        mutationError: action.value ? null : state.mutationError,
      };
    default:
      return state;
  }
}

function updateTaskTree(
  tasks: readonly CmmdBoardTask[],
  id: number,
  patch: Partial<CmmdBoardTask>,
): readonly CmmdBoardTask[] {
  return tasks.map((task) => {
    if (task.id === id) return { ...task, ...patch };
    const children = updateTaskTree(task.children, id, patch);
    return children.some((child, index) => child !== task.children[index])
      ? { ...task, children: [...children] }
      : task;
  });
}

export const INITIAL: TaskBoardState = {
  tasks: [],
  loading: true,
  error: null,
  mutationError: null,
  pendingTaskIds: new Set(),
  creating: false,
  workflow: null,
  workflowMutationBlocked: false,
};

export function errorMessage(cause: unknown, fallback: string): string {
  return (
    resolvePacketOverwriteMessage(cause) ??
    (cause instanceof Error && cause.message ? cause.message : fallback)
  );
}

/**
 * CMMD code for "this description edit would delete the card's packet".
 * @see CMMD server/services/forge/forge-packet-schema.ts (CMMD-2222)
 */
const PACKET_OVERWRITE_CODE = "packet_overwrite";

/**
 * Translate the server's packet-overwrite refusal into product copy.
 *
 * The server message is written for API callers and names an MCP tool, which
 * means nothing to someone typing in the sidebar. It is a refusal rather than a
 * failure — the edit was rejected precisely so the card's structured fields
 * survive — so the copy says what happened and what still works, instead of
 * reading as a lost save.
 */
export function resolvePacketOverwriteMessage(cause: unknown): string | null {
  const code = (cause as { readonly code?: unknown } | null)?.code;
  if (code !== PACKET_OVERWRITE_CODE) return null;
  return "This task's description is generated from its Forge packet, so editing it here would delete the packet's acceptance criteria and boundaries. Your text was not saved. Edit the packet instead.";
}

export async function readBackAssignment(
  projectId: string,
  taskId: number,
  subject: CmmdProjectTaskAssignmentSubject,
  operation: AssignmentOperation,
): Promise<{
  readonly canonical: ReadonlyArray<CmmdBoardTask>;
  readonly confirmed: boolean;
}> {
  const canonical = await api.listProjectBoard(projectId);
  return {
    canonical,
    confirmed: assignmentConfirmed({
      task: flattenTaskTree(canonical).find((task) => task.id === taskId),
      subject,
      operation,
    }),
  };
}

export function clearPendingAssignmentIfCurrent(
  pendingAssignments: Map<number, PendingAssignment>,
  taskId: number,
  fingerprint: string,
  idempotencyKey: string,
): void {
  const pending = pendingAssignments.get(taskId);
  if (pending?.fingerprint !== fingerprint || pending.idempotencyKey !== idempotencyKey) return;
  pendingAssignments.delete(taskId);
}

/** Shared by every assignment write so one intent maps to one idempotency key. */
export function assignmentFingerprint(
  subject: CmmdProjectTaskAssignmentSubject,
  operation: AssignmentOperation,
): string {
  return JSON.stringify({ subject, operation });
}

function assignmentFailureMessage(cause: unknown): string {
  return `Task was created, but assignment failed: ${errorMessage(cause, "assignment could not be saved")}. Assign it from task details.`;
}

export async function assignCreatedTask(input: {
  readonly projectId: string;
  readonly createdTaskId: number;
  readonly assignment: CmmdProjectTaskAssignmentSubject;
  readonly currentProjectIdRef: { readonly current: string };
  readonly pendingAssignments: Map<number, PendingAssignment>;
  readonly dispatch: (action: TaskBoardAction) => void;
}): Promise<void> {
  // A brand-new task has no assignees to preserve, so creation is always a
  // replace — the multi-assign operations only make sense on an existing set.
  const fingerprint = assignmentFingerprint(input.assignment, "replace");
  const idempotencyKey = crypto.randomUUID();
  input.pendingAssignments.set(input.createdTaskId, { fingerprint, idempotencyKey });
  try {
    await api.setProjectTaskAssignment(input.projectId, input.createdTaskId, {
      idempotencyKey,
      subject: input.assignment,
      operation: "replace",
    });
    input.pendingAssignments.delete(input.createdTaskId);
    return;
  } catch (cause) {
    try {
      const canonical = await api.listProjectBoard(input.projectId);
      if (input.currentProjectIdRef.current !== input.projectId) return;
      input.dispatch({ type: "reconcile", tasks: canonical });
      const confirmed = assignmentConfirmed({
        task: flattenTaskTree(canonical).find((task) => task.id === input.createdTaskId),
        subject: input.assignment,
        operation: "replace",
      });
      if (confirmed) input.pendingAssignments.delete(input.createdTaskId);
      else input.dispatch({ type: "mutation-error", message: assignmentFailureMessage(cause) });
    } catch {
      if (input.currentProjectIdRef.current !== input.projectId) return;
      input.dispatch({ type: "mutation-error", message: assignmentFailureMessage(cause) });
    }
  }
}

export function positionMove(move: ProjectTaskBoardMove): ProjectTaskPositionMove | null {
  return move.kind === "milestone" ? null : move.move;
}

function milestoneMove(move: ProjectTaskBoardMove): number | null | undefined {
  return move.kind === "position" ? undefined : move.milestoneId;
}

export function hasValidMoveNeighbors(
  tasks: readonly CmmdBoardTask[],
  position: ProjectTaskPositionMove | null,
): boolean {
  if (position === null) return true;
  const ids = new Set(tasks.map((task) => task.id));
  return (
    (position.beforeTaskId == null || ids.has(position.beforeTaskId)) &&
    (position.afterTaskId == null || ids.has(position.afterTaskId))
  );
}

function tailTaskOrder(task: CmmdBoardTask): number {
  return task.order < 0 ? -task.order : task.id;
}

function provisionalOrderBetween(after: CmmdBoardTask, before: CmmdBoardTask): number | null {
  const customOrder = after.order > 0;
  if (customOrder !== before.order > 0) return null;
  const afterOrder = customOrder ? after.order : tailTaskOrder(after);
  const beforeOrder = customOrder ? before.order : tailTaskOrder(before);
  const midpoint = (afterOrder + beforeOrder) / 2;
  return customOrder ? midpoint : -midpoint;
}

function provisionalOrderAfter(after: CmmdBoardTask): number {
  return after.order > 0 ? after.order + 0.5 : -tailTaskOrder(after) - 0.5;
}

function provisionalOrderBefore(before: CmmdBoardTask): number {
  return before.order > 0 ? before.order - 0.5 : -tailTaskOrder(before) + 0.5;
}

function provisionalOrderAtEnd(tasks: readonly CmmdBoardTask[]): number {
  const finalTailOrder = tasks.reduce((current, task) => Math.max(current, tailTaskOrder(task)), 0);
  return -(finalTailOrder + 0.5);
}

function nextDestinationTask(
  tasks: readonly CmmdBoardTask[],
  position: ProjectTaskPositionMove,
  after: CmmdBoardTask,
  movingTaskId: number,
): CmmdBoardTask | undefined {
  const destinationTasks = activeTasksInOrder(tasks).filter(
    (task) => task.id !== movingTaskId && task.status === position.toStatus,
  );
  const afterIndex = destinationTasks.findIndex((task) => task.id === after.id);
  if (afterIndex < 0) return undefined;
  return destinationTasks[afterIndex + 1];
}

export function provisionalTaskOrder(
  tasks: readonly CmmdBoardTask[],
  position: ProjectTaskPositionMove,
  movingTaskId: number,
): number {
  const after = tasks.find((task) => task.id === position.afterTaskId);
  let before = tasks.find((task) => task.id === position.beforeTaskId);
  if (before === undefined && after !== undefined) {
    before = nextDestinationTask(tasks, position, after, movingTaskId);
  }
  if (after && before) {
    const between = provisionalOrderBetween(after, before);
    if (between !== null) return between;
    return provisionalOrderAfter(after);
  }
  if (after) return provisionalOrderAtEnd(tasks);
  if (before) return provisionalOrderBefore(before);
  return provisionalOrderAtEnd(tasks);
}

export function optimisticWorkflowPatch(
  movingTask: CmmdBoardTask | undefined,
  position: ProjectTaskPositionMove,
): Pick<CmmdBoardTask, "workflowStageId"> & Partial<Pick<CmmdBoardTask, "workflowResumeStageId">> {
  const isInterrupted = position.toStatus === "blocked" || position.toStatus === "cancelled";
  return {
    workflowStageId: position.toStageId ?? null,
    ...(isInterrupted && movingTask?.workflowStageId != null
      ? { workflowResumeStageId: movingTask.workflowStageId }
      : {}),
  };
}

export function optimisticMovePatch(
  tasks: readonly CmmdBoardTask[],
  move: ProjectTaskBoardMove,
  movingTaskId: number,
): Partial<CmmdBoardTask> {
  const position = positionMove(move);
  const milestoneId = milestoneMove(move);
  const movingTask = tasks.find((task) => task.id === movingTaskId);
  return {
    ...(position
      ? {
          status: position.toStatus,
          ...optimisticWorkflowPatch(movingTask, position),
          order: provisionalTaskOrder(tasks, position, movingTaskId),
        }
      : {}),
    ...(milestoneId !== undefined ? { milestoneId } : {}),
  };
}

export function executeBoardMove(
  projectId: string,
  taskId: number,
  move: ProjectTaskBoardMove,
): readonly Promise<unknown>[] {
  const position = positionMove(move);
  const milestoneId = milestoneMove(move);
  return [
    ...(position ? [api.moveProjectBoardTask(projectId, taskId, position)] : []),
    ...(milestoneId !== undefined
      ? [api.updateProjectBoardTask(projectId, taskId, { milestoneId })]
      : []),
  ];
}
